import React, { useState } from "react";
import { Button } from "../../../../../components/button";
import { useToast } from "../../../../../components/toast";
import { CompetitionsAPI } from "../../../../../APIs/competitions.api";
import { ConfirmedTeamDataFResults } from "../../../../../types/team.types";
import { EditableField } from "../../../../../components/editable-field";

interface CreateBiggestFishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  competitionId: number;
  teams: ConfirmedTeamDataFResults[];
  fishSpecies: Array<{ id: number; name: string }>;
}

interface FormData {
  team_id: string;
  participant_id: string;
  fish_species_id: string;
  size_cm: string;
  points: string;
}

export function CreateBiggestFishModal({
  isOpen,
  onClose,
  onSuccess,
  competitionId,
  teams,
  fishSpecies,
}: CreateBiggestFishModalProps) {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    team_id: "",
    participant_id: "",
    fish_species_id: "",
    size_cm: "",
    points: "",
  });

  const selectedTeam = teams.find(
    (team) => team.id === parseInt(formData.team_id)
  );
  const participants = selectedTeam?.members || [];

  const handleInputChange = (e: React.ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "team_id") {
      setFormData((prev) => ({
        ...prev,
        participant_id: "",
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.team_id ||
      !formData.participant_id ||
      !formData.fish_species_id
    ) {
      addToast({
        type: "error",
        message: "Por favor completa todos los campos requeridos",
        location: "bottom-center",
      });
      return;
    }

    try {
      setLoading(true);

      const response = await CompetitionsAPI.postBiggestFishResults({
        competition_id: competitionId,
        team_id: parseInt(formData.team_id),
        participant_id: parseInt(formData.participant_id),
        fish_species_id: parseInt(formData.fish_species_id),
        size_cm: parseFloat(formData.size_cm),
        points: parseInt(formData.points),
      });

      if (response.statusCode === 201 || response.statusCode === 200) {
        onSuccess();
        resetForm();
      } else {
        throw new Error(response.message || "Error al crear el resultado");
      }
    } catch (error) {
      console.error("Error creating biggest fish result:", error);
      addToast({
        type: "error",
        message: "Error al crear el resultado de pieza mayor",
        location: "bottom-center",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      team_id: "",
      participant_id: "",
      fish_species_id: "",
      size_cm: "",
      points: "",
    });
  };

  const handleClose = () => {
    if (!loading) {
      resetForm();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            Agregar Pieza Mayor
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Team Selection */}
          <EditableField
            label="Equipo"
            value={formData.team_id}
            name="team_id"
            type="select"
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Seleccionar equipo"
            options={teams.map((team) => ({
              key: team.id.toString(),
              value: `${team.name}${
                team.institution?.name ? ` - ${team.institution.name}` : ""
              }`,
            }))}
          />

          {/* Participant Selection */}
          <EditableField
            label="Participante"
            value={formData.participant_id}
            name="participant_id"
            type="select"
            onChange={handleInputChange}
            required
            disabled={loading || !formData.team_id}
            placeholder={
              formData.team_id
                ? "Seleccionar participante"
                : "Primero selecciona un equipo"
            }
            options={participants.map((participant) => ({
              key: participant.user_id.toString(),
              value: `${participant.first_name} ${participant.last_name}`,
            }))}
          />

          {/* Fish Species Selection */}
          <EditableField
            label="Especie"
            value={formData.fish_species_id}
            name="fish_species_id"
            type="select"
            onChange={handleInputChange}
            required
            disabled={loading}
            placeholder="Seleccionar especie"
            options={fishSpecies.map((species) => ({
              key: species.id.toString(),
              value: species.name,
            }))}
          />
          {fishSpecies.length === 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Todas las especies ya tienen pieza mayor asignada
            </p>
          )}

          {/* Size */}
          <EditableField
            label="Tamaño (cm)"
            value={formData.size_cm}
            name="size_cm"
            type="number"
            step={"any"}
            onChange={handleInputChange}
            required
            disabled={loading}
            min={0}
            placeholder="Ej: 45.5"
          />

          {/* Points */}
          <EditableField
            label="Puntos"
            value={formData.points}
            name="points"
            type="number"
            onChange={handleInputChange}
            required
            disabled={loading}
            min={0}
            placeholder="Ej: 50"
          />

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              text="Cancelar"
              onClick={handleClose}
              variant="cancel"
              disabled={loading}
            />
            <Button
              type="submit"
              text={loading ? "Creando..." : "Crear Resultado"}
              variant="create"
              disabled={loading || fishSpecies.length === 0}
            />
          </div>
        </form>
      </div>
    </div>
  );
}
